/**
 * Per-operator preferences, and where "per-operator" actually means.
 *
 * Two people share a workstation in an ops room. If preferences are stored
 * under one key, the night shift inherits whatever the day shift chose — a
 * voice they did not ask for, reading out loud in a quiet room at 03:00. So
 * everything here is namespaced by the signed-in principal, and switching
 * account switches the whole set.
 *
 * It is still this browser only. That is a real limit and it is stated where an
 * operator can see it, rather than left to be discovered when they sit at a
 * different desk and find their settings gone. The platform has no per-user
 * preference store to write to; when it grows one, only `load` and `save` here
 * have to change.
 *
 * The defaults are the design as it shipped: the supplied portrait, hello and
 * goodbye spoken, no running commentary, the greeting on. Customisation is
 * something an operator opts into, never something that has already happened to
 * them — and if the portrait file is ever missing, the drawn one takes over
 * rather than leaving a hole.
 */

export type MarkVariant = "avatar" | "pulse" | "orbit" | "arc" | "image";

export interface VoicePrefs {
  /**
   * Reading every answer aloud. Still off by default — an operator mid-task
   * does not want a paragraph narrated at them.
   */
  enabled: boolean;
  /**
   * The greeting and the goodbye, spoken. On by default, and separate from the
   * setting above on purpose: these are two sentences at the start and end of a
   * shift, not a running commentary, and they are the moment a voice is worth
   * having. Both follow a press — entering the platform, or signing out — so
   * the browser has the activation it needs to make a sound at all.
   */
  greetAloud: boolean;
  /** SpeechSynthesisVoice.voiceURI; empty means the system default. */
  /**
   * Listening for her name in the background, so she can be spoken to without
   * being clicked first.
   *
   * This one keeps the microphone open. The browser asks permission once and
   * shows a recording indicator the whole time it runs. It is named plainly
   * wherever it appears for that reason — an operator turning on "hands-free"
   * has not been told what it costs.
   */
  wake: boolean;
  /**
   * Hands free: while she is open she is listening, and answers what she hears
   * without anything being pressed.
   *
   * On by default, because the alternative — press, speak, press, read — is
   * three actions for one question and nobody does it twice. The typing box
   * stays exactly as it was for anyone who would rather type; this only decides
   * whether the microphone is open alongside it.
   */
  handsFree: boolean;
  voiceURI: string;
  rate: number;
  pitch: number;
  /**
   * The language the microphone is transcribed as. Empty follows the chosen
   * voice, and failing that the browser's own language.
   *
   * It was `en-GB`, hard-coded in `listen.ts` and passed by nobody — so an
   * operator could pick an American or an Arabic voice for her to speak in
   * and still be transcribed as a British English speaker. Recognition
   * accuracy is mostly a question of matching the speaker, and at an
   * aerodrome in Madinah the assumption that everyone speaking to her has a
   * British accent is not a small one.
   */
  lang: string;
}

export interface Preferences {
  mark: MarkVariant;
  /** Only used when mark === "image". A data URI held in this browser. */
  markImage?: string;
  voice: VoicePrefs;
  /** The welcome on arrival. On by default; an operator who finds it noise can stop it. */
  greet: boolean;
  /**
   * Quiet: she answers when asked and says nothing otherwise.
   *
   * Not the same as switching her off. What it stops is everything she would
   * have said unbidden — the badge, the reading aloud, the consequence card
   * arriving while you are mid-task. What she works out still gets written to
   * the log, so an operator who goes quiet through a busy hour can open the
   * panel afterwards and read what they missed. Silence should cost you
   * nothing you cannot get back.
   */
  quiet: boolean;
  /**
   * Put away entirely: no picture, no corner, nothing.
   *
   * Distinct from quiet, which keeps her on screen and silent. This is for the
   * operator who wants the screen back, and it leaves one small tab to bring
   * her out again — a control that hides itself completely is a control nobody
   * can undo.
   */
  hidden: boolean;
}

export const DEFAULTS: Preferences = {
  mark: "image",
  voice: {
    enabled: false,
    greetAloud: true,
    wake: true,
    handsFree: true,
    voiceURI: "",
    rate: 1,
    pitch: 1,
    lang: "",
  },
  greet: true,
  quiet: false,
  hidden: false,
};

/**
 * The identity a preference belongs to. `subject` is the platform's stable id;
 * the display name is only a fallback, because two people can share one.
 */
export function principalKey(p: { subject?: string; name?: string }): string {
  return (p.subject || p.name || "anonymous").toLowerCase();
}

const PREFIX = "skylogics.assistant.prefs";

const keyFor = (who: string) => `${PREFIX}.${who}`;

export function loadPreferences(who: string): Preferences {
  try {
    const raw = localStorage.getItem(keyFor(who));
    if (!raw) return { ...DEFAULTS, voice: { ...DEFAULTS.voice } };
    const saved = JSON.parse(raw) as Partial<Preferences>;
    return {
      ...DEFAULTS,
      ...saved,
      voice: { ...DEFAULTS.voice, ...saved.voice },
    };
  } catch {
    return { ...DEFAULTS, voice: { ...DEFAULTS.voice } };
  }
}

export function savePreferences(who: string, p: Preferences): void {
  try {
    localStorage.setItem(keyFor(who), JSON.stringify(p));
  } catch {
    /* a browser refusing storage still gets a working session, just not a remembered one */
  }
}

/* ── when this operator was last here ──────────────────────────────────── */

/*
 * Kept beside the preferences because it is the same kind of thing: local,
 * per-person, and only ever used to make an arrival useful. It is what turns
 * "Good morning" into "Welcome back", and what bounds the digest of what has
 * happened since.
 */
const VISIT_PREFIX = "skylogics.assistant.lastVisit";

export function lastVisit(who: string): number | null {
  try {
    const v = localStorage.getItem(`${VISIT_PREFIX}.${who}`);
    const n = v ? Number(v) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function recordVisit(who: string, at: number = Date.now()): void {
  try {
    localStorage.setItem(`${VISIT_PREFIX}.${who}`, String(at));
  } catch {
    /* see above */
  }
}

/** "3 days ago", "yesterday", "earlier today" — for the welcome-back line. */
export function sinceWords(from: number, now: number = Date.now()): string {
  const ms = Math.max(0, now - from);
  const hours = ms / 3_600_000;
  if (hours < 1) return "a little while ago";
  if (hours < 6) return "earlier today";
  const days = Math.floor(hours / 24);
  if (days === 0) return "earlier today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "last week";
  return `${Math.floor(days / 7)} weeks ago`;
}
